import { Fragment, useContext } from 'react'
import ProviderSectionContainer, {
  PROVIDER_SECTION_IDS,
} from 'app/ProviderDetails/ProviderSectionContainer'
import {
  getDataFromProviderFilters,
  getProviderTitle,
  providerGlobalFilters,
} from 'app/ProviderDetails/utils'
import _isEmpty from 'lodash/isEmpty'
import styles from './index.module.scss'
import Icon from 'component_library/1_Atoms/Icon'
import { NAVY } from 'constants/colors'
import { RudderEvent } from 'constants/rudderEvents'
import RudderStackContext from 'context/common/RudderStackContext'
import logEvent from 'utils/logEvent'
import { getPractitionerType } from 'utils/common'

type AdditionalDetailsProps = {
  providerDetails: Provider
}

const AdditionalDetails: React.FC<AdditionalDetailsProps> = ({
  providerDetails,
}) => {
  const { practice, filters, licensedStates, about } = providerDetails
  const { rudderStackLoaded } = useContext(RudderStackContext)

  const languages = getDataFromProviderFilters(
    filters,
    providerGlobalFilters.Language
  )

  const { npi_number, license_number } = about ?? {}

  const practitionerType = getPractitionerType(
    getProviderTitle(providerDetails)
  )
  const otherLicensedStates = (licensedStates ?? [])
    .filter((s) => s.state !== practice?.state)
    .sort((a, b) => a.stateName.localeCompare(b.stateName))

  const handleLicensedInLinkClick = (toUrl: string) => {
    if (!rudderStackLoaded) {
      return
    }

    logEvent(RudderEvent.PROVIDER_PROFILE_LICENSED_IN_LINK_CLICKED, {
      provider_id: providerDetails.id,
      from_url: window.location.href,
      to_url: `${window.location.origin}${toUrl}`,
    })
  }

  return (
    <>
      {(!!practice?.state || !_isEmpty(languages)) && (
        <ProviderSectionContainer
          heading="Additional details"
          id={PROVIDER_SECTION_IDS.ADDITIONAL_DETAILS}
        >
          <div className={styles.add_details_grid}>
            {!_isEmpty(languages) && (
              <div className={styles.details_cell}>
                <div className={styles.details_icon}>
                  <img
                    src="/icons/globe-navy.svg"
                    width={24}
                    height={24}
                    alt="globe icon"
                    loading="lazy"
                  />
                </div>
                <div className={styles.details_content}>
                  <span className={styles.details_title}>
                    Languages spoken by this provider
                  </span>
                  <div className={styles.language_with_check_icon}>
                    {languages.map((language, index) => (
                      <Fragment key={index}>
                        <span>
                          <Icon icon="check" fill={NAVY} />
                          <span className={styles.lang_spoken}>
                            {language.value}
                          </span>
                        </span>
                      </Fragment>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {npi_number && (
              <div className={styles.details_cell}>
                <div className={styles.details_icon}>
                  <img
                    src="/icons/file.svg"
                    width={24}
                    height={24}
                    alt="file icon"
                    loading="lazy"
                  />
                </div>
                <div className={styles.details_content}>
                  <span className={styles.details_title}>NPI number</span>
                  <span>{npi_number}</span>
                </div>
              </div>
            )}

            {practice?.state && (
              <div className={styles.details_cell}>
                <div className={styles.details_icon}>
                  <img
                    src="/icons/practiceLocation2.svg"
                    width={24}
                    height={24}
                    alt="practice location icon"
                    loading="lazy"
                  />
                </div>
                <div className={styles.details_content}>
                  <span className={styles.details_title}>Licensed in</span>
                  <div className="~flex ~flex-col ~gap-2">
                    <span>{practice.stateName ?? practice.state}</span>
                    {otherLicensedStates.map((licensedState) => {
                      const toUrl = `/${practitionerType}/${licensedState.state.toLowerCase()}/${
                        licensedState.slug
                      }`
                      return (
                        <a
                          key={licensedState.state}
                          href={toUrl}
                          onClick={() => handleLicensedInLinkClick(toUrl)}
                          className="~text-black hover:~text-link ~underline ~underline-offset-2"
                        >
                          {licensedState.stateName}
                        </a>
                      )
                    })}
                  </div>
                </div>
              </div>
            )}

            {license_number && (
              <div className={styles.details_cell}>
                <div className={styles.details_icon}>
                  <img
                    src="/icons/licenseCard.svg"
                    width={24}
                    height={24}
                    alt="license card icon"
                    loading="lazy"
                  />
                </div>
                <div className={styles.details_content}>
                  <span className={styles.details_title}>License number</span>
                  <span>{license_number}</span>
                </div>
              </div>
            )}
          </div>
        </ProviderSectionContainer>
      )}
    </>
  )
}

export default AdditionalDetails
